import React from 'react'
import { debounce } from 'lodash-es'
import styled from '@emotion/styled'
import { Locale, NumberFormatOptions } from 'locale'
import { Decimal, KeyCodes } from 'utils'
import { BaseInput, Focusable, InputProps } from './BaseInput'
import { Field } from './Field'

const MAX_LENGTH_DEFAULT = 13

export interface NumericInputProps extends InputProps {
  id?: string
  className?: string
  value: number | null | undefined
  min?: number
  max?: number
  step?: number
  maxLength?: number
  isInteger?: boolean
  formatOptions?: NumberFormatOptions
  placeholder?: string
  updateOnBlurOnly?: boolean
  readOnly?: boolean
  disabled?: boolean
  onChange?: () => void
  onUpdate: (value: number | null | undefined, rawValue?: string | null | undefined, ...rest: any[]) => unknown
  onPaste?: (evt: React.ClipboardEvent) => unknown
  parseSpecialCharacters?: boolean
}

interface NumericInputState {
  isFocused: boolean
  currentValueStr: string | null | undefined
  currentValueNum: number | null | undefined
  previousValueNum: number | null | undefined
}

const StyledBaseInput = styled(BaseInput)`
  text-align: end;
  ::placeholder {
    text-align: left;
  }
`

export class NumericInput extends React.PureComponent<NumericInputProps, NumericInputState> implements Focusable {
  state = { isFocused: false, currentValueStr: null, currentValueNum: null, previousValueNum: null }

  static getDerivedStateFromProps(nextProps: NumericInputProps, state: NumericInputState): Partial<NumericInputState> | null {
    if (!state.isFocused || (nextProps.value !== state.currentValueNum && nextProps.value !== state.previousValueNum)) {
      return {
        currentValueStr: NumericInput.getDisplayedValue(nextProps.value, nextProps, state.isFocused),
        currentValueNum: nextProps.value,
        previousValueNum: nextProps.value
      }
    } else {
      return {
        previousValueNum: nextProps.value
      }
    }
  }

  static getDisplayedValue(value: number | null | undefined, props: NumericInputProps, isFocused: boolean) {
    let result = Locale.formatNumber(value, props.formatOptions)
    if (isFocused && !props.readOnly && !props.disabled) {
      result = result.replace(/[^\d.-]/g, '')
    }
    return result
  }

  static defaultProps = {
    isInteger: false
  }

  render() {
    const { id, className, min, max, readOnly, disabled, maxLength, placeholder, validationIssues, dataTestId, onPaste } = this.props
    const { currentValueStr } = this.state
    const displayedValue = currentValueStr || ''

    return (
      <StyledBaseInput
        ref={this.inputRef}
        id={id}
        data-test-id={dataTestId}
        className={className}
        readOnly={readOnly}
        disabled={disabled}
        validationIssues={validationIssues}
        autoComplete="off"
        spellCheck={false}
        type="text"
        value={displayedValue}
        min={min}
        max={max}
        maxLength={maxLength || MAX_LENGTH_DEFAULT}
        placeholder={placeholder}
        onClick={this.handleClick}
        onBlur={this.handleBlur}
        onFocus={this.handleFocus}
        onChange={this.handleChange}
        onKeyDown={this.handleKeyDown}
        onPaste={onPaste}
      />
    )
  }

  componentWillUnmount() {
    if (!this.props.updateOnBlurOnly) {
      this.doUpdateDebounced.flush()
    }
  }

  private inputRef = React.createRef<HTMLInputElement>()

  private handleClick = () => {
    this.setFocus()
  }

  private handleFocus = () => {
    const currentValueStr = NumericInput.getDisplayedValue(this.state.currentValueNum, this.props, true)
    this.setState({ isFocused: true, currentValueStr }, () => this.setFocus())
  }

  private handleBlur = () => {
    const currentValueStr = NumericInput.getDisplayedValue(this.state.currentValueNum, this.props, false)
    this.setState({ isFocused: false, currentValueStr })
    if (!this.props.updateOnBlurOnly) {
      this.doUpdateDebounced.flush()
    } else {
      this.doUpdate()
    }
  }

  private doUpdate = () => {
    const { value, onUpdate } = this.props
    const { currentValueNum, currentValueStr } = this.state

    if (currentValueNum !== value) {
      onUpdate(currentValueNum, currentValueStr)
    }
  }

  private doUpdateDebounced = debounce(() => this.doUpdate(), 750)

  private doChange = (newValueNum: number | null, newValueStr: string) => {
    this.setState({ currentValueNum: newValueNum, currentValueStr: newValueStr }, () => {
      if (this.props.onChange) {
        this.props.onChange()
      }

      if (!this.props.updateOnBlurOnly) {
        this.doUpdateDebounced()
      }
    })
  }

  private handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    const { isInteger } = this.props

    switch (evt.keyCode) {
      case KeyCodes.DECIMAL_POINT:
      case KeyCodes.PERIOD:
        if (isInteger) {
          evt.preventDefault()
        }
        break
      default:
        break
    }
  }

  /*
  Keeping these but commented for now in case we wish to revert behaviour in future

  private tryStepUp() {
    const { step, min, max, disabled, readOnly, formatOptions, value, isInteger } = this.props
    const { currentValueNum } = this.state
    const actualMax = Decimal.getMax(max, formatOptions?.maxDp)

    if (!disabled && !readOnly && canStepUp(currentValueNum, { min, max: actualMax })) {
      const targetStep = isInteger && step ? Math.ceil(step) : step
      const actualStep = resolveStep(targetStep, value, formatOptions || {})
      const newValueNum = stepUp(currentValueNum, actualStep, { min, max: actualMax })
      const newValueStr = Locale.formatNumber(newValueNum, formatOptions).replace(/[^\d.-]/g, '')
      this.doChange(newValueNum, newValueStr)
    }
  }

  private tryStepDown() {
    const { step, min, max, disabled, readOnly, formatOptions, value, isInteger } = this.props
    const { currentValueNum } = this.state
    const actualMin = Decimal.getMin(min, formatOptions?.maxDp)

    if (!disabled && !readOnly && canStepDown(currentValueNum, { min: actualMin, max })) {
      const targetStep = isInteger && step ? Math.floor(step) : step
      const actualStep = resolveStep(targetStep, value, formatOptions || {})
      const newValueNum = stepDown(currentValueNum, actualStep, { min: actualMin, max })
      const newValueStr = Locale.formatNumber(newValueNum, formatOptions).replace(/[^\d.-]/g, '')
      this.doChange(newValueNum, newValueStr)
    }
  }
  */

  private handleChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
    const { formatOptions } = this.props

    try {
      let newValueNum = null

      if (currentTarget.value !== '') {
        if (this.props.parseSpecialCharacters) {
          newValueNum = Decimal.parse(currentTarget.value.replace(/[,%]/g, ''))
        } else {
          newValueNum = Decimal.parse(currentTarget.value)
        }

        if (formatOptions) {
          if (
            formatOptions.maxDp &&
            Math.floor(newValueNum) !== newValueNum &&
            newValueNum.toString().split('.')[1].length > formatOptions.maxDp
          ) {
            return
          }

          if (formatOptions.flipSign) {
            newValueNum = Decimal.multiply(newValueNum, -1)
          } else if (formatOptions.absoluteIncrease) {
            newValueNum = Decimal.subtract(newValueNum, 100)
          } else if (formatOptions.absoluteDecrease) {
            newValueNum = Decimal.subtract(100, newValueNum)
          }

          if (formatOptions.decimalShift) {
            newValueNum = Decimal.shift(newValueNum, -formatOptions.decimalShift)
          }
        }
      }

      this.doChange(newValueNum, currentTarget.value)
    } catch (err) {
      /* Just ignore the keystroke */
    }
  }

  public setFocus() {
    if (this.inputRef.current) {
      this.inputRef.current.focus()
      this.inputRef.current.select()
    }
  }

  public getValue() {
    return this.state.currentValueNum
  }
}

export const NumericInputField = Field(NumericInput)
